/**
 * Custom language for conditional display of questions.
 * See CheckCondition.java
 */
import assert from "utils/assert";
import { isMultipartQuestion } from "./helper";
import { isExcludedFn } from "api/issb/Question";

const unset = "unset";
/** true for any version of unset */
export function isUnset(value) {
  if (value === null || value === undefined || value === "" || value === unset) return true;
  if (Array.isArray(value)) {
    if (value.length === 0) return true;
    return value.every(isUnset);
  }
  return false;
}
/**
 *
 * @param {[]} results
 * @returns true/false/unset
 */
function condenseResults(results) {
  const truthy = results.filter((r) => r && !isUnset(r)).length > 0;
  if (truthy) return true;
  const unsetty = results.filter((r) => isUnset(r)).length > 0;
  if (unsetty) return unset;
  return false;
}
/**
 * Handles the custom language:
 * ()s
 * AND OR
 * LHS = RHS
 * LHS contains RHS
 * function() or function(arg1) (but not ,s so not function(arg1, arg2))
 *  -- This is specifically for answer("clause-reference") lookups
 * "quoted value" or 'quoted value'
 * #TAG for tag checks
 * answer("ref")[PartName] for multipart/part lookup
 *
 * @param {string} condition e.g. 'answer("a") = "Always" AND answer("b") = "chocolate"'
 * @returns {object[]} parse tree e.g. ["AND", ["=", 'answer("a")', 'Always'], ["=", "answer("b")', 'chocolate']]
 */
function parseCondition(condition) {
  const tokens = [];
  const state = { index: 0, quote: null };
  while (true) {
    let token = readNextToken(condition, state);
    if (!token) {
      break;
    }
    tokens.push(token);
  }
  const parseTree = parseCondition2(tokens, { index: 0 }, []);
  // console.log("parseCondition2", JSON.stringify(parseTree));
  return parseTree;
}

function parseCondition2(tokens, state, parseTree) {
  while (true) {
    const token = tokens[state.index];
    state.index++;
    if (token === undefined) {
      return parseTree;
    }
    if (token === "AND" || token === "OR") {
      if (parseTree[0] !== token) {
        if (parseTree[0] === "AND" || parseTree[0] === "OR") {
          throw new Error("Conflicting AND / OR  in " + tokens.join(" "));
        }
        parseTree.splice(0, 0, token);
      }
      continue;
    }
    if (token === "NOT") {
      const subTree = parseCondition2(tokens, state, []);
      parseTree.push(["NOT", subTree]);
      continue;
    }
    if (token === "=" || token === "==" || token === "contains") {
      // HACK allow == as well as =
      // create an =s test
      const previous = parseTree.pop();
      const nextToken = tokens[state.index]; // Note: does not support complex RHS e.g.  x = (a + b)
      state.index++;
      const operator = token === "==" ? "=" : token; // standardise == to =
      parseTree.push([operator, previous, nextToken]);
      continue;
    }
    if (token === "(") {
      const subTree = parseCondition2(tokens, state, []);
      parseTree.push(subTree);
      continue;
    }
    if (token[token.length - 1] === "(") {
      // e.g. answer(
      const subTree = parseCondition2(tokens, state, []);
      parseTree.push([token, subTree]);
      continue;
    }
    if (token === ")") {
      return parseTree;
    }
    // Support for part[] operator (e.g. answer("7.1")[PartName])
    if (token === "[") {
      const subTree = parseCondition2(tokens, state, []);
      // previous is the thing to index (e.g. answer("7.1"))
      const prev = parseTree.pop();
      const lookup = subTree[0];
      parseTree.push(["part[]", prev, lookup]);
      continue;
    }
    if (token === "]") {
      return parseTree;
    }
    // Support for #TAG
    if (token[0] === "#") {
      parseTree.push(token);
      continue;
    }
    parseTree.push(token);
  }
}

function readNextToken(condition, state) {
  let word = "";
  while (true) {
    // consume a character
    const c = condition[state.index];
    state.index++;
    if (c === undefined) {
      // console.log("end of condition", word, state);
      return word || null;
    }
    if (c === '"' || c === "'") {
      if (state.quote === c) {
        state.quote = null;
        return word;
      } else if (!state.quote) {
        state.quote = c;
        continue;
      }
    }
    if (state.quote) {
      word += c;
      continue;
    }
    if (c === " ") {
      if (word) {
        return word;
      }
      continue; // skip
    }
    if (c === "(") {
      if (word) {
        // e.g. answer(
        return word + c;
      }
      return c;
    }
    if (c === ")" || c === "=") {
      if (word) {
        state.index--; // return the ) next time
        return word;
      }
      return c;
    }
    if (c === "[" || c === "]") {
      if (word) {
        state.index--;
        return word;
      }
      return c;
    }
    word += c;
  }
}

// for debug
window.readNextToken = readNextToken;
window.parseCondition = parseCondition;

/**
 *
 * @param {string} condition
 * @param {object} questionData
 * @param {object} allQuestionDataByRef
 * @param {string[]} tags (optional)
 * @param {object} part (optional) For per-part conditions
 * @param {object[]} answerParts (optional) For per-part conditions - the rest of this question
 * @param {boolean} uncondensed (optional) If true, return the uncondensed results -- eg an array of values instead of true/false/unset.
 * @returns true/false/unset/value true if the part is relevant, false otherwise. Unset if some conditions are unset. Array as multipart questions can return multiple values.
 */
export function checkCondition({
  condition,
  questionData,
  allQuestionDataByRef,
  tags,
  part,
  answerParts,
  uncondensed,
}) {
  if (!condition) return [true];
  try {
    // HACK: provide the fresh local questionData over the server-side saved version
    // Note: questionData.answer (which gets checked here) lags behind the parsed QAPart[] answer-values object that gets edited. A save will update .answer
    if (questionData?.issbReference) {
      allQuestionDataByRef = Object.assign({}, allQuestionDataByRef, { [questionData.issbReference]: questionData });
    }
    const parseTree = parseCondition(condition);
    const values = checkCondition2(parseTree, questionData, allQuestionDataByRef, tags, part, answerParts);
    const result = uncondensed ? values : condenseResults(values);
    return result;
  } catch (error) {
    console.error(condition, error);
    return unset;
  }
}

/**
 * Evaluate a condition parseTree
 * @param {object[]} parseTree
 * @param {object} questionData
 * @param {object} allQuestionDataByRef e.g. {"7.15": "Yes", "7.16": "No"}
 * @param {string[]} tags (optional)
 * @returns {object[]} [true/false/unset/value] true if the part is relevant, false otherwise. Unset if some conditions are unset. Array as multipart questions can return multiple values.
 */
function checkCondition2(parseTree, questionData, allQuestionDataByRef, tags, part, answerParts) {
  if (typeof parseTree === "string") {
    // #TAG support
    if (parseTree[0] === "#") {
      if (!tags) return [false];
      const tag = canon(parseTree.slice(1));
      const ctags = tags.map(canon);
      if (ctags.includes(tag)) {
        return [true];
      } else {
        return [false];
      }
    }
    return [parseTree]; // return as value
  }
  const operator = parseTree[0];
  if (Array.isArray(operator)) {
    assert(parseTree.length === 1, parseTree);
    return checkCondition2(operator, questionData, allQuestionDataByRef, tags, part, answerParts);
  }
  // test operators
  if (operator === "=" || operator === "contains") {
    const left = checkCondition2(parseTree[1], questionData, allQuestionDataByRef, tags);
    const right = checkCondition2(parseTree[2], questionData, allQuestionDataByRef, tags);
    // handle arrays for multipart
    let slvs = left.map(canon);
    slvs = slvs.filter((lv) => lv !== unset);
    let srvs = right.map(canon);
    srvs = srvs.filter((rv) => rv !== unset);
    if (slvs.length === 0) {
      return [unset];
    }
    if (srvs.length === 0) {
      return [unset];
    }
    const result = checkCondition3_test(operator, slvs, srvs);
    return [result];
  } // ./test operators
  if (operator === "AND") {
    for (let i = 1; i < parseTree.length; i++) {
      const results = checkCondition2(parseTree[i], questionData, allQuestionDataByRef, tags, part, answerParts);
      const result1 = condenseResults(results);
      if (result1 === false) return [false];
      if (result1 === unset) return [unset];
    }
    return [true];
  }
  if (operator === "OR") {
    let unsetCount = 0;
    for (let i = 1; i < parseTree.length; i++) {
      const subCondition = parseTree[i];
      const result = checkCondition2(subCondition, questionData, allQuestionDataByRef, tags, part, answerParts);
      //   console.log("OR", subCondition, "result", result);
      const result1 = condenseResults(result);
      if (result1 === true) return [true];
      if (result1 === unset) unsetCount++;
    }
    if (unsetCount > 0) {
      return [unset];
    }
    return [false];
  }
  if (operator === "NOT") {
    const subCondition = parseTree[1];
    const result = checkCondition2(subCondition, questionData, allQuestionDataByRef, tags, part, answerParts);
    const result1 = condenseResults(result);
    if (result1 === true) return [false]; // NOT = flip true to false
    if (result1 === unset) return [unset]; // unset is unset
    return [true]; // NOT = flip false to true
  }
  if (operator === "answer(") {
    const answerRef = parseTree[1];
    return checkCondition3_answer(answerRef, allQuestionDataByRef);
  }
  // part[] operator for multipart/label lookup
  if (operator === "part[]") {
    let questionLabel = parseTree[1]; // e.g. [answer, 7.1]
    const bigResult = questionLabel
      ? checkCondition2(parseTree[1], questionData, allQuestionDataByRef, tags, part, answerParts) // a question lookup
      : [answerParts]; // a per-part condition referring to its own question
    const partLabel = parseTree[2];
    // bigResult is an array of values, need to get the label list
    const qdata = allQuestionDataByRef && allQuestionDataByRef[questionLabel[1]];
    let labels = checkCondition3_getLabels(qdata); // array of strings and string[] (for tables)
    if (!labels) {
      return [unset];
    }
    let i = labels.map(canon).indexOf(canon(partLabel));
    let i2;
    if (i === -1) {
      if (partLabel.match(/^[0-9]+$/)) {
        // try as a number, converting from 1-based to 0-based index
        i = parseInt(partLabel) - 1;
      } else if (partLabel.match(/^[a-z]$/)) {
        // try as a letter, converting to 0-based index
        i = partLabel.charCodeAt(0) - "a".charCodeAt(0);
      } else {
        // try contains, which can match list-of-columns
        for (let j = 0; j < labels.length; j++) {
          if (canon(JSON.stringify(labels[j])).includes(canon(partLabel))) {
            i = j;
            if (Array.isArray(labels[j])) {
              i2 = labels[j].map(canon).indexOf(canon(partLabel));
            }
            break;
          }
        }
        if (i === -1) {
          console.log("Unrecognised label", partLabel, "in", labels, "from", parseTree, questionData);
          return [unset];
        }
      }
    }
    if (i < 0 || i >= bigResult.length) {
      return [unset];
    }
    let resulti = bigResult[i];
    if (i2 !== undefined) {
      let resulti2 = resulti.map((row) => row[i2]?.answerValue);
      resulti = resulti2;
    }
    return [resulti];
  } // ./part[]
  // #TAG as operator
  if (operator[0] === "#") {
    if (!tags) return [false];
    const tag = canon(operator.slice(1));
    const ctags = tags.map(canon);
    if (ctags.includes(tag)) {
      return [true];
    } else {
      return [false];
    }
  }
  throw new Error("Unrecognised condition " + JSON.stringify(parseTree));
}

function checkCondition3_getLabels(qdata) {
  if (!qdata || !qdata.answer || !isMultipartQuestion(qdata)) return null;
  try {
    const answerParts = JSON.parse(qdata.answer);
    const labels = answerParts.map((ap) => {
      if (Array.isArray(ap?.answerValue)) {
        const row0 = Array.isArray(ap?.answerValue[0]) ? ap?.answerValue[0] : ap?.answerValue; //first row of table, + paranoid fallback
        const columnLabels = row0.map((v) => v?.questionPart);
        return columnLabels;
      } else {
        return ap?.questionPart;
      }
    });
    return labels;
  } catch (e) {
    console.log("checkCondition3_getLabels error", qdata, e);
    return null;
  }
}

/**
 *
 * @param {*} operator
 * @param {*} slvs
 * @param {*} srvs
 * @returns true/false/unset
 */
function checkCondition3_test(operator, slvs, srvs) {
  // the test - once we've got down to single values
  let valueTest;
  if (operator === "=") {
    valueTest = (lv, rv) => lv === rv;
  } else if (operator === "contains") {
    valueTest = (lv, rv) => {
      if (typeof lv === "string" && lv.includes(rv)) {
        return true;
      }
      // HACK: workaround for canon producing "contains true"
      // An alternative would be to avoid canon with contains
      if (rv === "true" || rv === true) {
        if (lv === true) return true;
        if (typeof lv === "string" && lv.toLowerCase().includes("yes")) return true;
      }
      if (rv === "false" || rv === false) {
        if (lv === false) return true;
        if (typeof lv === "string" && lv.toLowerCase().includes("no")) return true;
      }
      return false;
    };
  } else {
    throw new Error("Unrecognised operator: " + operator);
  }
  // true if any overlap
  let unsetCount = 0;
  for (const lv of slvs) {
    // one value or an array? e.g. a select multiple can have value string[]
    // make them all arrays
    let lva = Array.isArray(lv) ? lv : [lv];
    for (const lvi of lva) {
      if (isUnset(lvi)) {
        unsetCount++;
        continue;
      }
      for (const rv of srvs) {
        let rva = Array.isArray(rv) ? rv : [rv];
        for (const rvi of rva) {
          if (isUnset(rvi)) {
            unsetCount++;
            continue;
          }
          if (valueTest(lvi, rvi)) {
            // console.log("checkCondition3_test true", operator, lvi, rvi);
            return true;
          }
        }
      }
    }
  }
  if (unsetCount > 0) {
    // console.log("checkCondition3_test unset", operator, slvs, srvs);
    return unset;
  }
  // console.log("checkCondition3_test false", operator, slvs, srvs);
  return false;
}

/**
 *
 * @param {*} answerRef
 * @param {*} allQuestionDataByRef
 * @returns [true/false/unset] Excluded questions return [false]
 */
function checkCondition3_answer(answerRef, allQuestionDataByRef) {
  const qdata = allQuestionDataByRef[answerRef];
  if (!qdata) return [unset];
  const answer = qdata.answer;
  if (isExcludedFn(qdata)) return [false];
  if (isUnset(answer)) return [unset];
  if (!isMultipartQuestion(qdata)) {
    return [answer];
  }
  try {
    const answerParts = qdata._answerParts || JSON.parse(answer);
    let answerPartValues = answerParts.map((part) => part?.answerValue);
    // unsets?
    answerPartValues = answerPartValues.map((v) => (isUnset(v) ? unset : v));
    // remove any trailing nulls
    while (
      answerPartValues.length &&
      (answerPartValues[answerPartValues.length - 1] === null ||
        answerPartValues[answerPartValues.length - 1] === undefined)
    ) {
      answerPartValues.pop();
    }
    return answerPartValues;
  } catch (e) {
    console.error("checkCondition3_answer error", answerRef, answer, e);
    return [unset];
  }
}

/**
 *
 * @param {*} value
 * @returns {string} lowercase, yes/no => true/false, null => "unset"
 */
function canon(value) {
  if (value === true) return "true";
  if (value === false) return "false";
  if (isUnset(value)) return unset;
  if (Array.isArray(value)) {
    return value.map(canon);
  }
  const lvalue = value.toLowerCase();
  if (lvalue === "true" || lvalue === "yes") return "true";
  if (lvalue === "false" || lvalue === "no") return "false";
  return lvalue;
}

function isSet(value) {
  if (Array.isArray(value)) {
    return value.every(isSet);
  }
  return value !== "unset" && value !== null && value !== undefined;
}

/**
 * @param {object} props
 * @param {string} props.condition
 * @param {object} props.part
 * @param {object[]} props.answerParts
 * @param {object} props.questionData
 * @param {object} props.allQuestionDataByRef
 * @param {string[]} props.tags (optional)
 * @param {boolean} props.uncondensed (optional) If true, return the uncondensed results -- eg an array of values instead of true/false/unset.
 * @returns true/false/unset true if the part is relevant, false otherwise
 */
export function checkQuestionPartCondition({
  condition,
  part,
  answerParts,
  questionData,
  allQuestionDataByRef,
  tags,
  uncondensed,
}) {
  if (!condition) return true;
  let checked = checkCondition({ condition, questionData, allQuestionDataByRef, tags, part, answerParts, uncondensed });
  return checked;
}

const conditionPattern = /\[(.*)\]\s*$/;

/**
 * Extract the condition from an option string e.g. "Apples [answer('1.1')[a] = 'Fruit']"
 * @param {string} option
 * @returns {string} the condition or null if not found
 */
export function getConditionFilter(option) {
  if (!option) return null;
  if (typeof option !== "string") {
    console.log("getConditionFilter skip non-string", option);
    return null;
  }
  const s = option.indexOf("["); // Should we switch to {}s for similarity to jsx and other template languages??
  if (s === -1) return null;
  const e = option.lastIndexOf("]");
  if (e === -1 || e < s) return null;
  const condition = option.substring(s + 1, e);
  return condition;
}

export function prettyPrintCondition(condition) {
  let conditionText = condition;
  // replace OR/AND with or/and
  conditionText = conditionText.replace(/OR/g, "or").replace(/AND/g, "and");
  // replace answer("x") with answer x
  conditionText = conditionText.replace(/answer\("([^"]+)"\)/g, "answer $1");
  conditionText = conditionText.replace(/answer\('([^']+)'\)/g, "answer $1");
  return conditionText;
}
